/**
 * @file This file contains the MiniMergeController class, which handles the game
 * logic for the Mini Merge game mode.
 * @module MiniMergeController
 */

import { GameController } from './GameController.js';

/**
 * Description placeholder
 *
 * @export
 * @class MiniMergeController
 * @typedef {MiniMergeController}
 * @extends {GameController}
 */
export class MiniMergeController extends GameController {
    /**
 * Creates an instance of MiniMergeController.
 *
 * @constructor
 * @param {*} dependencies 
 */
constructor(dependencies) {
        super(dependencies);
        
        /**
         * The three main slots on the Mini Merge grid.
         * @type {Array<object|null>}
         */
        this.slots = [null, null, null];

        /**
         * The items that the player has chosen to hold.
         * @type {Array<boolean>}
         */
        this.held = [false, false, false];

        /**
         * Tracks the order in which items were held to manage the "max 2 holds" rule.
         * @type {Array<number>}
         */
        this.heldOrder = [];

        /**
         * The cost in turns for each nudge.
         * @type {number}
         */
        this.nudgeCost = 1;

        /**
         * The index of the currently selected item in the queue. -1 if none.
         * @type {number}
         */
        this.selectedQueueIndex = -1;

        /**
         * Flag to control when the player is allowed to hold items.
         * @type {boolean}
         */
        this.canHold = false;

        /**
         * Tracks which slots were held on the previous turn to prevent re-holding.
         * @type {Set<number>}
         */
        this.previouslyHeldSlots = new Set();
    }

    /**
     * Overrides the base initController to add Mini Merge specific setup.
     */
    initController() {
        super.initController(); // This calls createGrid()
        
        // Initialize the hand with 3 random items, independently of the queue.
        for (let i = 0; i < 3; i++) {
            this.slots[i] = this.gameState.getRandomQueueItem();
        }

        this.ui.updateMiniMergeGrid(this.slots, this.held, this.gameState.activeGameConfig);
        this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, -1);
        this._updateNudgeButtonState();
        this.previouslyHeldSlots.clear(); // No holds at the very start.

        // Check for an initial match right away.
        this.checkForMerge();
        this.canHold = true; // Allow holding right at the start.
    }

    /**
     * Overrides the default click handler for the Mini Merge mode.
     * @param {Event} event - The click event.
     */
    handleCellClick(event) {
        if (this.gameState.isGameOver || this.isProcessingTurn) return;
 
        const cellElement = event.target.closest('.gem');
        if (!cellElement) return;
 
        const index = parseInt(cellElement.dataset.col, 10) - this.grid.bufferSize;
 
        if (index < 0 || index >= 3) return;
 
        // If an item from the queue is selected, try to place it.
        if (this.selectedQueueIndex !== -1) {
            const queueItem = this.gameState.itemQueue[this.selectedQueueIndex];

            if (this.slots[index] !== null) {
                // Scenario 2: A filled slot is clicked while a queue item is selected.
                // If the keys are different, deselect the queue item.
                if (queueItem && queueItem.key !== this.slots[index].key) { // Mismatch
                    this.selectedQueueIndex = -1;
                    this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, this.selectedQueueIndex);
                } else if (this.canHold) { // Match, so toggle hold
                    this.toggleHold(index);
                    // After toggling hold, we should deselect the queue item and stop.
                    this.selectedQueueIndex = -1;
                    this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, this.selectedQueueIndex);
                    return;
                }
                return;
            }
            const itemToPlace = queueItem;
            if (!itemToPlace) return; // Clicked an empty queue slot

            this.slots[index] = itemToPlace;
            this.held[index] = false; // Reset hold status when a new item is placed

            this.gameState.itemQueue[this.selectedQueueIndex] = null; // Leave an empty spot in the queue
            this.selectedQueueIndex = -1; // Reset selection
            this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, this.selectedQueueIndex);

            // Once an item is placed, the player can no longer hold until the next nudge.
            this.canHold = false;
            this.previouslyHeldSlots.clear(); // Placing an item resets the hold lock.
            this.ui.updateMiniMergeGrid(this.slots, this.held, this.gameState.activeGameConfig);

            if (this.gameState.itemQueue.every(item => item === null)) {
                for (let i = 0; i < 5; i++) {
                    this.gameState.itemQueue[i] = this.gameState.getRandomQueueItem();
                }
                this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, -1);
            }

            this.checkForMerge(false); // Do not allow holding after a placement merge.
            this._updateNudgeButtonState();
        } else {
            // If no queue item is selected, toggle hold, but only if allowed.
            if (this.canHold) {
                this.toggleHold(index);
            }
        }
    }

    /**
     * Toggles the 'held' state of a slot.
     * @param {number} index - The index of the slot to toggle.
     */
    toggleHold(index) {
        // You can't hold an empty slot.
        if (this.previouslyHeldSlots.has(index)) {
            return; // Cannot re-hold a slot that was held on the previous nudge.
        }
        if (!this.slots[index]) return;
    
        const isCurrentlyHeld = this.held[index];
    
        if (isCurrentlyHeld) {
            // Un-hold the item
            this.held[index] = false;
            this.heldOrder = this.heldOrder.filter(i => i !== index);
        } else {
            // If an item is already held, and it's a different type, un-hold it.
            if (this.heldOrder.length > 0) {
                const firstHeldIndex = this.heldOrder[0];
                if (this.slots[firstHeldIndex].key !== this.slots[index].key) {
                    this.held[firstHeldIndex] = false;
                    this.heldOrder.shift();
                }
            }
            // Hold the new item. If we're already holding 2, un-hold the oldest one.
            if (this.heldOrder.length >= 2) {
                const oldestHeldIndex = this.heldOrder.shift(); // Removes and returns the first element
                this.held[oldestHeldIndex] = false;
            }
            this.held[index] = true;
            this.heldOrder.push(index);
        }
        this.ui.updateMiniMergeGrid(this.slots, this.held, this.gameState.activeGameConfig);
        this._updateNudgeButtonState();
    }

    /**
     * Executes a "nudge" or turn. Unheld slots are replaced with new items from the queue.
     * Then, it checks for a merge.
     */
    nudge() {
        this.game.audioManager.play('click');
        this.gameState.numberOfTurnsTaken += this.nudgeCost;
        this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsTaken);
 
        if (this.isProcessingTurn) return;
        this.isProcessingTurn = true;
 
        let selectedItemPlaced = false;
        // 1. Refill un-held slots. Prioritize the selected queue item.
        for (let i = 0; i < 3; i++) {
            if (!this.held[i]) {
                if (this.selectedQueueIndex !== -1 && !selectedItemPlaced) {
                    const itemToPlace = this.gameState.itemQueue[this.selectedQueueIndex];
                    if (itemToPlace) {
                        this.slots[i] = itemToPlace;
                        this.gameState.itemQueue[this.selectedQueueIndex] = null; // Consume the item
                        selectedItemPlaced = true;
                    } else {
                        // The selected queue slot was empty, fill with random.
                        this.slots[i] = this.gameState.getRandomQueueItem();
                    }
                } else {
                    // No item selected or selected item already placed, fill with random.
                    this.slots[i] = this.gameState.getRandomQueueItem();
                }
            }
        }
        
        // 2. After filling the hand, completely refresh the queue.
        for (let i = 0; i < 5; i++) {
            this.gameState.itemQueue[i] = this.gameState.getRandomQueueItem();
        }

        // Track which slots were just held so they can't be immediately re-held.
        this.previouslyHeldSlots = new Set(this.heldOrder);

        // 3. After the nudge, clear the holds for the next turn.
        this.held = [false, false, false];
        this.heldOrder = [];
 
        // Deselect any queue item after the nudge.
        this.selectedQueueIndex = -1;

        this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, -1);
        this.ui.updateMiniMergeGrid(this.slots, this.held, this.gameState.activeGameConfig);

        // After a nudge, the player is allowed to hold items again.
        this.canHold = true;
        this._updateNudgeButtonState();

        // After nudging, check if a merge was created.
        const mergeStarted = this.checkForMerge(true); // Allow holding if a nudge causes a merge.

        // If no merge was started, we end the turn manually.
        if (!mergeStarted) {
            this.isProcessingTurn = false;
            this.endTurn();
        }
    }

    /**
     * Handles a click on an item in the queue.
     * @param {number} index - The index of the clicked item in the queue.
     */
    handleQueueClick(index) {
        if (this.gameState.isGameOver || this.isProcessingTurn) return;
 
        const clickedQueueItem = this.gameState.itemQueue[index];
 
        // If an empty queue slot is clicked, just deselect.
        if (!clickedQueueItem) {
            this.selectedQueueIndex = -1;
        } else {
            // Scenario 1: If items are held and the new selection is different, clear holds.
            if (this.heldOrder.length > 0) {
                const heldItemKey = this.slots[this.heldOrder[0]].key;
                if (heldItemKey !== clickedQueueItem.key) {
                    this.held = [false, false, false];
                    this.heldOrder = [];
                    this.ui.updateMiniMergeGrid(this.slots, this.held, this.gameState.activeGameConfig);
                }
            }
 
            // Toggle selection of the queue item.
            this.selectedQueueIndex = this.selectedQueueIndex === index ? -1 : index;
        }
 
        this._updateNudgeButtonState();
        this.ui.updateQueue(this.gameState.itemQueue, -1, null, this.gameState.activeGameConfig.gameMode, this.selectedQueueIndex);
    }

    /**
     * Checks for a 3-item merge in the hand and processes it.
     * @param {boolean} allowHoldAfterMerge - If true, the player can hold the newly merged item.
     * @returns {boolean} True if a merge was found and is being processed, false otherwise.
     */
    checkForMerge(allowHoldAfterMerge = false) {
        if (this.slots[0] && this.slots[1] && this.slots[2] &&
            this.slots[0].key === this.slots[1].key && this.slots[1].key === this.slots[2].key) {
            
            this.isProcessingTurn = true;
            const group = new Set(['0,0', '0,1', '0,2']); // Dummy coordinates for animation
            
            this.ui.animateMerge(group, { r: 0, c: 1 }, null, this.gameState.activeGameConfig, () => {
                const upgradePath = this.gameState.activeGameConfig.upgradePath;
                const currentTier = upgradePath.findIndex(item => item.key === this.slots[0].key);
                
                if (currentTier > -1 && currentTier < upgradePath.length - 1) {
                    const newItem = upgradePath[currentTier + 1];
                    this.slots = [null, newItem, null];
                    this.held = [false, false, false];   // Deselect all items after a merge.
                    this.heldOrder = []; // Clear the hold order.
                    this.gameState.addScore(this.gameState.calculateMergeScore(3, currentTier, false));
                    this.ui.updateScore(this.gameState.score); // Score is global, no need for config
                    this.ui.updateMiniMergeGrid(this.slots, this.held, this.gameState.activeGameConfig); // Update UI after merge

                    this.canHold = allowHoldAfterMerge;
                }
                this.isProcessingTurn = false;
                this.endTurn();
                this._updateNudgeButtonState();
            });
            return true; // A merge was found and is being processed.
        }

        return false; // No merge was found.
    }

    /**
     * Updates the enabled/disabled state of the Nudge button based on the current hand.
     */
    _updateNudgeButtonState() {
        const handFull = this.slots.filter(item => item !== null).length === 3;
        this.ui.toggleNudgeButton(handFull);
    }
}
